import React, { useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';
import CustomDatePicker from '../components/common/CustomDatePicker';
import Pagination from '../components/common/Pagination';
import { startOfMonth, endOfMonth, subYears, isWithinInterval, differenceInCalendarDays, addDays, format, getDay, getISOWeek } from 'date-fns';
import { de } from 'date-fns/locale';

const { FiDownload, FiRefreshCw, FiCalendar, FiColumns, FiActivity, FiBarChart2, FiTrendingUp, FiTrendingDown } = FiIcons;

function Sales() {
  const { data } = useLexware();

  // -- Primary Range (Aktuell) --
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  // -- Comparison Range (Vergleich: Vorjahr gleicher Monat) --
  // Geändert von subMonths(..., 1) zu subYears(..., 1)
  const [compareRange, setCompareRange] = useState({
    from: startOfMonth(subYears(new Date(), 1)),
    to: endOfMonth(subYears(new Date(), 1))
  });

  // Chart Configuration
  const [chartType, setChartType] = useState('bar');
  const [viewMode, setViewMode] = useState('daily');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Filter Logic
  const filteredSales = useMemo(() => {
    const sales = data.sales || [];
    if (!dateRange.from || !dateRange.to) return sales;
    return sales.filter(sale => {
      const saleDate = new Date(sale['RG-Datum']);
      return isWithinInterval(saleDate, { start: dateRange.from, end: dateRange.to });
    });
  }, [data.sales, dateRange]);

  const filteredCompareSales = useMemo(() => {
    const sales = data.sales || [];
    if (!compareRange.from || !compareRange.to) return [];
    return sales.filter(sale => {
      const saleDate = new Date(sale['RG-Datum']);
      return isWithinInterval(saleDate, { start: compareRange.from, end: compareRange.to });
    });
  }, [data.sales, compareRange]);

  const paginatedSales = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredSales.slice(start, start + pageSize);
  }, [filteredSales, currentPage, pageSize]);

  // Calculations
  const calcTotals = (salesList) => {
    const netto = salesList.reduce((sum, s) => sum + (s._netto || 0), 0);
    const tax = salesList.reduce((sum, s) => sum + (s['Steuerbetrag'] || 0), 0);
    return { netto, tax, brutto: netto + tax };
  };

  const currentTotals = calcTotals(filteredSales);
  const compareTotals = calcTotals(filteredCompareSales);

  const getDiffStats = (current, previous) => {
    const diff = current - previous;
    const percent = previous !== 0 ? (diff / previous) * 100 : 0;
    return { diff, percent, isPositive: diff >= 0 };
  };

  const stats = {
    netto: getDiffStats(currentTotals.netto, compareTotals.netto),
    tax: getDiffStats(currentTotals.tax, compareTotals.tax),
    brutto: getDiffStats(currentTotals.brutto, compareTotals.brutto)
  };

  // Chart Option Generation
  const chartOption = useMemo(() => {
    if (!dateRange.from || !dateRange.to || !compareRange.from || !compareRange.to) return null;
    
    const sales = data.sales || [];
    let categories = [];
    let seriesPrimaryData = [];
    let seriesCompareData = [];

    // Simple Daily Sum logic for chart
    const getDailySums = (range) => {
      const map = {};
      sales.forEach(sale => {
        const d = new Date(sale['RG-Datum']);
        if (isWithinInterval(d, { start: range.from, end: range.to })) {
          // Wir nutzen hier nur Tag und Monat für das Mapping, um Jahre übereinander zu legen
          // Aber Vorsicht: Wenn Ranges unterschiedlich lang sind, ist das schwierig.
          // Einfachste Lösung für Visualisierung: Index-basiert (Tag 1, Tag 2...)
          const key = format(d, 'yyyy-MM-dd');
          map[key] = (map[key] || 0) + (sale._netto || 0);
        }
      });
      return map;
    };

    const primarySums = getDailySums(dateRange);
    const compareSums = getDailySums(compareRange);

    const daysCount = differenceInCalendarDays(dateRange.to, dateRange.from) + 1;
    
    for (let i = 0; i < daysCount; i++) {
        const d = addDays(dateRange.from, i);
        categories.push(format(d, 'dd.MM.', { locale: de }));
        
        seriesPrimaryData.push(primarySums[format(d, 'yyyy-MM-dd')] || 0);
        
        // Für Vergleich: Wir nehmen den i-ten Tag aus der Vergleichs-Range
        // Das funktioniert gut, wenn beide Ranges gleich lang sind (z.B. ganzer Monat)
        const dComp = addDays(compareRange.from, i);
        seriesCompareData.push(compareSums[format(dComp, 'yyyy-MM-dd')] || 0);
    }

    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['Aktuell', 'Vergleich'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '5%', containLabel: true },
      xAxis: { 
        type: 'category', 
        data: categories,
      },
      yAxis: { type: 'value' },
      series: [
        { 
          name: 'Aktuell', 
          type: chartType, 
          data: seriesPrimaryData, 
          itemStyle: { color: '#3b82f6' } 
        },
        { 
          name: 'Vergleich', 
          type: chartType, 
          data: seriesCompareData, 
          itemStyle: { color: '#9ca3af' },
          lineStyle: { type: 'dashed' }
        }
      ]
    };
  }, [data.sales, dateRange, compareRange, chartType, viewMode]);

  const StatBox = ({ title, current, diffStats, colorClass }) => (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col justify-between">
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase mb-1">{title}</p>
        <p className={`text-3xl font-black ${colorClass}`}>
          €{current.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
        </p>
      </div>
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2">
        <div className={`flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${diffStats.isPositive ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          <SafeIcon icon={diffStats.isPositive ? FiTrendingUp : FiTrendingDown} />
          <span>{diffStats.isPositive ? '+' : ''}{diffStats.percent.toFixed(1)}%</span>
        </div>
        <span className={`text-xs font-medium ${diffStats.isPositive ? 'text-green-600' : 'text-red-500'}`}>
          ({diffStats.isPositive ? '+' : ''}€{diffStats.diff.toLocaleString('de-DE', { minimumFractionDigits: 2 })})
        </span>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Umsatzübersicht (Netto)</h1>
        
        <div className="flex flex-col sm:flex-row items-center gap-3 bg-white p-2 rounded-xl border border-gray-200 shadow-sm">
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 px-1 uppercase">Aktuell</span>
            <CustomDatePicker 
              selectedDateRange={dateRange} 
              onChange={r => { setDateRange(r); setCurrentPage(1); }} 
            />
          </div>
          
          <SafeIcon icon={FiRefreshCw} className="text-gray-300 hidden sm:block" />
          
          <div className="flex flex-col">
            <span className="text-[10px] font-bold text-gray-400 px-1 uppercase">Vergleich</span>
            <CustomDatePicker 
              selectedDateRange={compareRange} 
              onChange={r => setCompareRange(r)} 
            />
          </div>

          <button className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 h-[42px] mt-auto flex items-center gap-2">
            <SafeIcon icon={FiDownload} /> <span className="hidden xl:inline">Export</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatBox title="Netto Gesamt" current={currentTotals.netto} diffStats={stats.netto} colorClass="text-blue-600" />
        <StatBox title="Steuer" current={currentTotals.tax} diffStats={stats.tax} colorClass="text-gray-700" />
        <StatBox title="Brutto" current={currentTotals.brutto} diffStats={stats.brutto} colorClass="text-gray-400" />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900">Umsatzvergleich</h3>
          <div className="flex bg-gray-100 p-1 rounded-lg">
            <button onClick={() => setChartType('line')} className={`p-2 rounded-md ${chartType === 'line' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}><SafeIcon icon={FiActivity} /></button>
            <button onClick={() => setChartType('bar')} className={`p-2 rounded-md ${chartType === 'bar' ? 'bg-white shadow text-blue-600' : 'text-gray-400'}`}><SafeIcon icon={FiBarChart2} /></button>
          </div>
        </div>
        <div className="h-[400px]">
          {chartOption && <ReactECharts option={chartOption} style={{ height: '100%' }} />}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
          <h3 className="font-bold text-gray-900">Details (Aktueller Zeitraum)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 uppercase text-[10px] font-bold border-b">
              <tr>
                <th className="px-6 py-3">Datum</th>
                <th className="px-6 py-3">RG-Nr.</th>
                <th className="px-6 py-3">Kunde</th>
                <th className="px-6 py-3 text-right">Netto</th>
                <th className="px-6 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedSales.map((s, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-6 py-3 font-mono">{format(new Date(s['RG-Datum']), 'dd.MM.yyyy')}</td>
                  <td className="px-6 py-3 text-gray-500">{s['RG-Nr.']}</td>
                  <td className="px-6 py-3 font-medium">{s['Firmenname']}</td>
                  <td className="px-6 py-3 text-right font-bold">€{s._netto.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                  <td className="px-6 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${s['bezahlt am'] ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {s['bezahlt am'] ? 'Bezahlt' : 'Offen'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Integration */}
        <Pagination 
          currentPage={currentPage}
          totalItems={filteredSales.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </div>
  );
}

export default Sales;